import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Button from "../components/common/Button";
import Dropdown from "../components/common/Dropdown";
import ErrorAlert from "../components/common/ErrorAlert";
import FormWrapper from "../components/common/FormWrapper";
import Input from "../components/common/Input";
import PasswordField from "../components/common/PasswordField";
import AuthShell from "../components/layout/AuthShell";
import { useAuth } from "../hooks/useAuth";
import roleService from "../services/roleService";
import { validateRegister } from "../utils/validators";

const initialValues = {
  username: "",
  fullName: "",
  email: "",
  password: "",
  confirmPassword: "",
  roleId: "",
};

function RegisterPage() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [values, setValues] = useState(initialValues);
  const [roleOptions, setRoleOptions] = useState([]);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const loadRoles = async () => {
      try {
        const rolePayload = await roleService.getRoles();
        const roleItems = Array.isArray(rolePayload)
          ? rolePayload
          : Array.isArray(rolePayload?.data)
          ? rolePayload.data
          : Array.isArray(rolePayload?.items)
          ? rolePayload.items
          : [];

        const options = roleItems
          .map((role) => {
            const name = role?.roleName || role?.name;
            const id = role?.roleId || role?.id;
            if (!name || !id) return null;
            return { label: name, value: id };
          })
          .filter(Boolean);

        setRoleOptions(options);

        // Auto-select "Operator" (usually ID 2) if it exists
        const operatorRole = options.find(
          (opt) => opt.label.toLowerCase() === "operator"
        );
        if (operatorRole) {
          setValues((prev) => ({ ...prev, roleId: operatorRole.value }));
        }
      } catch (error) {
        console.error("Failed to load roles:", error);
      }
    };

    loadRoles();
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setValues((currentValues) => ({ ...currentValues, [name]: value }));
    setErrors((currentErrors) => ({ ...currentErrors, [name]: "" }));
    setSubmitError("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const validationErrors = validateRegister(values);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      setIsSubmitting(true);
      setSubmitError("");
      await register(values);
      navigate("/", { replace: true });
    } catch (error) {
      setErrors((currentErrors) => ({ ...currentErrors, ...error.fieldErrors }));
      setSubmitError(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthShell>
      <FormWrapper
        footer={
          <p className="text-sm text-[#4a5568] dark:text-[#b6bfd1]">
            Already have access?{" "}
            <Link className="font-semibold text-[#b97a32] transition-colors hover:text-[#9f6829]" to="/login">
              Return to sign in
            </Link>
          </p>
        }
      >
        <form className="space-y-5" onSubmit={handleSubmit} noValidate>
          <div className="grid gap-5 md:grid-cols-2">
            <Input
              label="Username"
              name="username"
              value={values.username}
              onChange={handleChange}
              placeholder="operator_user"
              error={errors.username}
              autoComplete="username"
            />

            <Dropdown
              label="Role"
              name="roleId"
              value={values.roleId}
              onChange={handleChange}
              options={roleOptions}
              placeholder="Select role"
              error={errors.roleId}
            />
          </div>

          <Input
            label="Full name"
            name="fullName"
            value={values.fullName}
            onChange={handleChange}
            placeholder="System Operator"
            error={errors.fullName}
            autoComplete="name"
          />

          <Input
            label="Business email"
            name="email"
            type="email"
            value={values.email}
            onChange={handleChange}
            placeholder="operator@example.com"
            error={errors.email}
            autoComplete="email"
          />

          <div className="grid gap-5 md:grid-cols-2">
            <PasswordField
              label="Password"
              name="password"
              value={values.password}
              onChange={handleChange}
              placeholder="Operator@123"
              error={errors.password}
              hint="8-50 chars, including upper, lower, number, and symbol."
              autoComplete="new-password"
            />
            <PasswordField
              label="Confirm password"
              name="confirmPassword"
              value={values.confirmPassword}
              onChange={handleChange}
              placeholder="Re-enter your password"
              error={errors.confirmPassword}
              autoComplete="new-password"
            />
          </div>

          {submitError ? <ErrorAlert message={submitError} /> : null}

          <Button type="submit" className="w-full bg-[#111c33] hover:bg-[#0b1324] focus:ring-[#111c33]" isLoading={isSubmitting}>
            Create account
          </Button>
        </form>
      </FormWrapper>
    </AuthShell>
  );
}

export default RegisterPage;
